import React, { useState, useEffect, useMemo } from 'react';
import { X, User, Bell, Palette, Shield, CreditCard, Save, AlertCircle, Check, Download, Trash2, Lock, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { ArtCategory } from '../types';
import PaymentsTab from './PaymentsTab';
import SubscriptionTab from './SubscriptionTab';
import SecurityTab from './SecurityTab';
import { useLanguage } from '../context/LanguageContext';
import type { Language } from '../i18n/translations';
import { privacyService } from '../services/privacyService';
import { authService } from '../services/authService';

interface SettingsModalProps {
  isOpen: boolean;
  initialTab?: string;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, initialTab = 'account', onClose }) => {
  const { user, refreshUser } = useAuth();
  const { theme, updateTheme } = useTheme();
  const { select, t, language, setLanguage, availableLanguages } = useLanguage();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getLanguageLabel = (lang: Language) => t(`language.${lang}`, lang.toUpperCase());

  const handleLanguageChange = (lang: Language) => {
    setError('');
    setLanguage(lang);
    setSuccess(select({
      es: 'Idioma actualizado',
      en: 'Language updated',
      fr: 'Langue mise à jour',
      it: 'Lingua aggiornata',
      pt: 'Idioma atualizado',
      de: 'Sprache aktualisiert',
      ar: 'تم تحديث اللغة',
    }));
  };

  const categoryLabels = useMemo(
    () => ({
      music: t('categories.music', select({ es: 'Música', en: 'Music' })),
      writing: t('categories.writing', select({ es: 'Escritura', en: 'Writing' })),
      tattoo: t('categories.tattoo', select({ es: 'Tatuajes', en: 'Tattoos' })),
      digital_design: t('categories.digital_design', select({ es: 'Diseño Digital', en: 'Digital Design' })),
      fashion_design: t('categories.fashion_design', select({ es: 'Diseño de Moda', en: 'Fashion Design' })),
      photography: t('categories.photography', select({ es: 'Fotografía', en: 'Photography' })),
      sculpture: t('categories.sculpture', select({ es: 'Escultura', en: 'Sculpture' })),
      painting: t('categories.painting', select({ es: 'Pintura', en: 'Painting' })),
      dance: t('categories.dance', select({ es: 'Danza', en: 'Dance' })),
      filmmaking: t('categories.filmmaking', select({ es: 'Cine', en: 'Filmmaking' })),
      makeup: t('categories.makeup', select({ es: 'Maquillaje', en: 'Makeup' })),
    }),
    [language, select, t]
  );

  // Account settings
  const [accountForm, setAccountForm] = useState({
    full_name: '',
    bio: '',
    location: '',
    website: '',
    art_categories: [] as ArtCategory[],
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    push_notifications: true,
    like_notifications: true,
    comment_notifications: true,
    follow_notifications: true,
    collaboration_notifications: true,
    sale_notifications: true,
    marketing_emails: false,
  });

  // Theme settings
  const [themeForm, setThemeForm] = useState({
    mode: theme.mode,
    primaryColor: theme.primaryColor,
    fontSize: theme.fontSize,
    fontFamily: theme.fontFamily,
  });

  // Privacy settings
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: 'public' as 'public' | 'followers' | 'private',
    show_online_status: true,
    allow_messages: 'everyone' as 'everyone' | 'followers' | 'none',
    show_in_search: true,
    analytics_tracking: true,
  });

  const [downloadingData, setDownloadingData] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [authEmail, setAuthEmail] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadSettings();
    }
  }, [isOpen, user]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!isOpen) return;

    const loadAuthEmail = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        setAuthEmail(data.user?.email ?? null);
      } catch (err) {
        console.error('Error fetching auth user for delete email:', err);
        setAuthEmail(null);
      }
    };

    loadAuthEmail();
  }, [isOpen]);

  const loadSettings = async () => {
    if (!user) return;

    setAccountForm({
      full_name: user.full_name || '',
      bio: user.bio || '',
      location: user.location || '',
      website: user.website || '',
      art_categories: user.art_categories || [],
    });

    // Load other settings from database or localStorage
    const savedNotificationSettings = localStorage.getItem('notification-settings');
    if (savedNotificationSettings) {
      setNotificationSettings(JSON.parse(savedNotificationSettings));
    }

    const savedPrivacySettings = localStorage.getItem('privacy-settings');
    if (savedPrivacySettings) {
      setPrivacySettings(JSON.parse(savedPrivacySettings));
    }

    setThemeForm({
      mode: theme.mode,
      primaryColor: theme.primaryColor,
      fontSize: theme.fontSize,
      fontFamily: theme.fontFamily,
    });
  };

  const handleSaveAccount = async () => {
    if (!user) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: accountForm.full_name,
          bio: accountForm.bio,
          location: accountForm.location,
          website: accountForm.website,
          art_categories: accountForm.art_categories,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setSuccess(select({ es: 'Perfil actualizado exitosamente', en: 'Profile updated successfully' }));

      // Refresh user data safely
      if (refreshUser) {
        try {
          await refreshUser();
        } catch (refreshErr) {
          console.warn('Error refreshing user session:', refreshErr);
        }
      }
    } catch (err) {
      const baseMessage = select({
        es: 'Error al actualizar el perfil',
        en: 'Error updating profile'
      });
      const detail = err instanceof Error && err.message ? `: ${err.message}` : '';
      setError(`${baseMessage}${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = () => {
    localStorage.setItem('notification-settings', JSON.stringify(notificationSettings));
    setSuccess(select({ es: 'Configuración de notificaciones guardada', en: 'Notification settings saved' }));
  };

  const handleSaveTheme = () => {
    updateTheme(themeForm);
    setSuccess(select({ es: 'Configuración de tema aplicada', en: 'Theme preferences applied' }));
  };

  const handleSavePrivacy = () => {
    localStorage.setItem('privacy-settings', JSON.stringify(privacySettings));
    setSuccess(select({ es: 'Configuración de privacidad guardada', en: 'Privacy settings saved' }));
  };

  const handleDownloadAccountData = async (format: 'json' | 'excel' | 'pdf' = 'json') => {
    if (!user) return;

    setDownloadingData(true);
    setError('');
    setSuccess('');

    try {
      await privacyService.downloadAccountData(format);
      setSuccess(
        t(
          'privacy.export.success',
          select({
            es: `Descarga de ${format.toUpperCase()} iniciada. Revisa tu carpeta de descargas.`,
            en: `${format.toUpperCase()} download started. Check your downloads folder.`,
          })
        )
      );
    } catch (err) {
      console.error('Error downloading account data:', err);
      const fallbackMessage = select({
        es: 'No se pudieron descargar tus datos.',
        en: 'Unable to download your data.',
      });
      const localized = t('privacy.export.error', fallbackMessage);
      const extra = err instanceof Error && err.message ? ` ${err.message}` : '';
      setError(`${localized}${extra}`.trim());
    } finally {
      setDownloadingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    const targetEmail = (user?.email || authEmail || '').trim().toLowerCase();
    const inputEmail = deleteConfirmation.trim().toLowerCase();

    if (!targetEmail || inputEmail !== targetEmail) {
      setError(
        t(
          'privacy.delete.confirmError',
          select({
            es: 'Debes introducir tu correo para confirmar la eliminación.',
            en: 'Enter your email to confirm deletion.',
          })
        )
      );
      return;
    }

    setDeletingAccount(true);
    setError('');
    setSuccess('');

    try {
      await authService.deleteAccount();
      setSuccess(
        t(
          'privacy.delete.success',
          select({
            es: 'Tu cuenta se eliminó correctamente.',
            en: 'Your account was deleted successfully.',
          })
        )
      );
      setDeleteConfirmation('');
      onClose();
    } catch (err) {
      console.error('Error deleting account:', err);
      const fallbackMessage = select({
        es: 'No pudimos eliminar tu cuenta.',
        en: 'We could not delete your account.',
      });
      const localized = t('privacy.delete.error', fallbackMessage);
      const extra = err instanceof Error && err.message ? ` ${err.message}` : '';
      setError(`${localized}${extra}`.trim());
    } finally {
      setDeletingAccount(false);
    }
  };

  const toggleArtCategory = (category: ArtCategory) => {
    setAccountForm(prev => ({
      ...prev,
      art_categories: prev.art_categories.includes(category)
        ? prev.art_categories.filter(c => c !== category)
        : [...prev.art_categories, category]
    }));
  };

  const normalizedTargetEmail = (user?.email || authEmail || '').trim().toLowerCase();
  const canDeleteAccount = Boolean(
    normalizedTargetEmail && deleteConfirmation.trim().toLowerCase() === normalizedTargetEmail
  );

  const tabs = useMemo(
    () => [
      { id: 'account', label: select({ es: 'Cuenta', en: 'Account' }), icon: User },
      { id: 'subscription', label: select({ es: 'Suscripción', en: 'Subscription' }), icon: CreditCard },
      { id: 'notifications', label: select({ es: 'Notificaciones', en: 'Notifications' }), icon: Bell },
      { id: 'appearance', label: select({ es: 'Apariencia', en: 'Appearance' }), icon: Palette },
      { id: 'security', label: select({ es: 'Seguridad', en: 'Security' }), icon: Lock },
      { id: 'privacy', label: select({ es: 'Privacidad', en: 'Privacy' }), icon: Shield },
      { id: 'payments', label: select({ es: 'Pagos', en: 'Payments' }), icon: CreditCard },
      { id: 'payout_methods', label: select({ es: 'Métodos de cobro', en: 'Payout methods' }), icon: DollarSign },
    ],
    [select]
  );

  const themeOptions = useMemo(
    () => [
      {
        value: 'light' as const,
        label: select({ es: 'Claro', en: 'Light' }),
        description: select({ es: 'Ideal para espacios luminosos y trabajo diurno.', en: 'Great for bright environments and daytime work.' }),
        emoji: '☀️',
      },
      {
        value: 'dark' as const,
        label: select({ es: 'Oscuro', en: 'Dark' }),
        description: select({ es: 'Descansa la vista en entornos con poca luz.', en: 'Easier on the eyes in low-light environments.' }),
        emoji: '🌙',
      },
      {
        value: 'auto' as const,
        label: select({ es: 'Automático', en: 'Automatic' }),
        description: select({ es: 'Se adapta automáticamente al modo de tu sistema.', en: 'Follows your system preference automatically.' }),
        emoji: '🔄',
      },
    ],
    [select]
  );

  if (!isOpen) return null;

  const colorOptions = [
    '#9333ea',
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#84cc16',
  ] as const;

  const fontOptions = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat'] as const;

  const previewBackgroundClass =
    themeForm.mode === 'dark'
      ? 'bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800'
      : themeForm.mode === 'light'
        ? 'bg-gradient-to-br from-white via-gray-50 to-gray-100'
        : 'bg-gradient-to-br from-gray-950 via-gray-800 to-white';

  const previewBorderClass = themeForm.mode === 'dark' ? 'border-gray-800' : 'border-gray-200';
  const previewTextClass = themeForm.mode === 'light' ? 'text-gray-900' : 'text-white';
  const previewMutedTextClass = themeForm.mode === 'light' ? 'text-gray-600' : 'text-white/70';
  const previewBadgeClass = themeForm.mode === 'light' ? 'bg-purple-100 text-purple-700' : 'bg-white/10 text-white/80';
  const previewHighlightClass = themeForm.mode === 'light' ? 'bg-white/80 text-gray-700' : 'bg-white/10 text-white/80';
  const currentThemeMeta = themeOptions.find((option) => option.value === themeForm.mode);
  const previewAccentStyle = {
    backgroundColor: themeForm.primaryColor,
    boxShadow: `0 18px 45px -18px ${themeForm.primaryColor}`,
    color: '#ffffff',
  } as React.CSSProperties;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 md:p-4 z-50">
      <div className="bg-white w-full h-full md:rounded-2xl md:w-full md:max-w-7xl md:max-h-[90vh] md:h-auto overflow-hidden">
        <div className="flex flex-col md:flex-row h-full md:max-h-[90vh]">
          {/* Sidebar */}
          <div className="w-full md:w-80 bg-gray-50 border-b md:border-r md:border-b-0 border-gray-200 p-4 md:p-6 flex-shrink-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{select({ es: 'Configuración', en: 'Settings' })}</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors touch-target"
                aria-label={select({ es: 'Cerrar', en: 'Close' })}
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex md:flex-col space-x-2 md:space-x-0 md:space-y-1 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 md:w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors touch-target whitespace-nowrap ${activeTab === tab.id
                      ? 'bg-purple-100 text-purple-700'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <Icon size={18} />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-8 min-h-0">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                <Check size={16} />
                {success}
              </div>
            )}

            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{select({ es: 'Información de la cuenta', en: 'Account information' })}</h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Nombre completo', en: 'Full name' })}
                    </label>
                    <input
                      type="text"
                      value={accountForm.full_name}
                      onChange={(e) => setAccountForm(prev => ({ ...prev, full_name: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                      placeholder={select({ es: 'Nombre y apellidos', en: 'Full name' })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Ubicación', en: 'Location' })}
                    </label>
                    <input
                      type="text"
                      value={accountForm.location}
                      onChange={(e) => setAccountForm(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                      placeholder={select({ es: 'Ciudad, país', en: 'City, country' })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Biografía', en: 'Bio' })}
                  </label>
                  <textarea
                    rows={3}
                    value={accountForm.bio}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, bio: e.target.value }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-base"
                    placeholder={select({ es: 'Cuéntanos sobre ti...', en: 'Tell us about yourself...' })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Sitio web', en: 'Website' })}
                  </label>
                  <input
                    type="url"
                    value={accountForm.website}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                    placeholder={select({ es: 'https://tu-sitio-web.com', en: 'https://your-website.com' })}
                  />
                </div>

                {(user?.user_type === 'artist' || user?.user_type === 'admin') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {select({ es: 'Especialidades artísticas', en: 'Art specialties' })}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {Object.entries(categoryLabels).map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleArtCategory(key as ArtCategory)}
                          className={`p-3 text-sm border rounded-lg transition-all text-left touch-target ${accountForm.art_categories.includes(key as ArtCategory)
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                            }`}
                        >
                          {label}
                        </button>
                      ))}

                    </div>
                  </div>
                )}

                <button
                  onClick={handleSaveAccount}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 touch-target text-base font-medium"
                >
                  <Save size={16} />
                  {loading
                    ? select({ es: 'Guardando...', en: 'Saving...' })
                    : select({ es: 'Guardar cambios', en: 'Save changes' })}
                </button>
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && <SubscriptionTab />}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{select({ es: 'Configuración de notificaciones', en: 'Notification settings' })}</h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{select({ es: 'Notificaciones por email', en: 'Email notifications' })}</p>
                      <p className="text-sm text-gray-500">{select({ es: 'Recibe actualizaciones importantes por email', en: 'Get important updates via email' })}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings.email_notifications}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, email_notifications: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{select({ es: 'Notificaciones push', en: 'Push notifications' })}</p>
                      <p className="text-sm text-gray-500">{select({ es: 'Recibe notificaciones en tiempo real', en: 'Receive notifications in real time' })}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings.push_notifications}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, push_notifications: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-4">{select({ es: 'Tipos de notificaciones', en: 'Notification types' })}</h4>

                    <div className="space-y-3">
                      {[{ key: 'like_notifications', label: select({ es: 'Likes en tus publicaciones', en: 'Likes on your posts' }), desc: select({ es: 'Cuando alguien le da like a tu contenido', en: 'When someone likes your content' }) },
                      { key: 'comment_notifications', label: select({ es: 'Comentarios', en: 'Comments' }), desc: select({ es: 'Cuando alguien comenta en tus publicaciones', en: 'When someone comments on your posts' }) },
                      { key: 'follow_notifications', label: select({ es: 'Nuevos seguidores', en: 'New followers' }), desc: select({ es: 'Cuando alguien empieza a seguirte', en: 'When someone starts following you' }) },
                      { key: 'collaboration_notifications', label: select({ es: 'Colaboraciones', en: 'Collaborations' }), desc: select({ es: 'Invitaciones y actualizaciones de proyectos', en: 'Invitations and project updates' }) },
                      { key: 'sale_notifications', label: select({ es: 'Ventas', en: 'Sales' }), desc: select({ es: 'Cuando alguien compra tu arte', en: 'When someone buys your art' }) }
                      ].map((setting) => (
                        <div key={setting.key} className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{setting.label}</p>
                            <p className="text-sm text-gray-500">{setting.desc}</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={notificationSettings[setting.key as keyof typeof notificationSettings] as boolean}
                              onChange={(e) => setNotificationSettings(prev => ({ ...prev, [setting.key]: e.target.checked }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSaveNotifications}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 touch-target text-base font-medium"
                >
                  <Save size={16} />
                  {select({ es: 'Guardar configuración', en: 'Save settings' })}
                </button>
              </div>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{select({ es: 'Personalización de apariencia', en: 'Appearance customization' })}</h3>

                <div className={`relative overflow-hidden rounded-2xl border ${previewBorderClass} shadow-sm transition-colors`}>
                  <div className={`${previewBackgroundClass} absolute inset-0`} aria-hidden="true"></div>
                  <div className={`relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between p-6 sm:p-8 ${previewTextClass}`}>
                    <div className="space-y-3 max-w-xl">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold uppercase tracking-widest rounded-full ${previewBadgeClass}`}>
                        {select({ es: 'Vista previa', en: 'Preview' })}
                      </span>
                      <h4 className="text-xl font-semibold">
                        {select({ es: 'Así se verá la interfaz', en: 'This is how the interface will look' })}
                      </h4>
                      <p className={`text-sm ${previewMutedTextClass}`}>
                        {currentThemeMeta?.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-xs font-medium">
                        <span className="uppercase tracking-widest" style={{ color: themeForm.primaryColor }}>
                          {select({ es: 'Color principal', en: 'Primary color' })}: {themeForm.primaryColor}
                        </span>
                        <span className={`${previewMutedTextClass} uppercase tracking-widest`}>
                          {select({ es: 'Tipografía', en: 'Typeface' })}: {themeForm.fontFamily} · {themeForm.fontSize}px
                        </span>
                      </div>
                    </div>
                    <div className="flex w-full sm:w-auto flex-col items-start gap-3">
                      <button
                        type="button"
                        className="px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/40"
                        style={previewAccentStyle}
                      >
                        {select({ es: 'Botón primario', en: 'Primary button' })}
                      </button>
                      <div className={`px-4 py-3 rounded-xl text-sm font-medium shadow ${previewHighlightClass}`}>
                        {select({ es: 'Los elementos destacados usarán este color.', en: 'Highlighted elements will use this accent color.' })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
                    <header className="space-y-1">
                      <h4 className="text-base font-semibold text-gray-900">
                        {select({ es: 'Idioma de la interfaz', en: 'Interface language' })}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {select({ es: 'Selecciona en qué idioma quieres ver menús y mensajes.', en: 'Choose the language for menus and messages.' })}
                      </p>
                    </header>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {availableLanguages.map((langOption) => {
                        const isActive = language === langOption;
                        return (
                          <button
                            key={langOption}
                            type="button"
                            onClick={() => handleLanguageChange(langOption)}
                            aria-pressed={isActive}
                            className={`rounded-xl border px-4 py-4 text-left transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 ${isActive
                              ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-sm'
                              : 'border-gray-200 text-gray-700 hover:border-purple-300 hover:shadow-sm'
                              }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-lg font-semibold">{getLanguageLabel(langOption)}</span>
                              {isActive && <Check size={16} />}
                            </div>
                            <span className="mt-1 block text-xs text-gray-500 uppercase tracking-widest">
                              {langOption.toUpperCase()}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500">
                      {select({ es: 'Tu preferencia se guardará en este dispositivo.', en: 'Your preference is saved on this device.' })}
                    </p>
                  </section>

                  <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
                    <header className="space-y-1">
                      <h4 className="text-base font-semibold text-gray-900">{select({ es: 'Tema de la aplicación', en: 'App theme' })}</h4>
                      <p className="text-sm text-gray-500">{select({ es: 'Alterna entre modos claro, oscuro o automático.', en: 'Switch between light, dark or automatic modes.' })}</p>
                    </header>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {themeOptions.map((option) => {
                        const isActive = themeForm.mode === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setThemeForm((prev) => ({ ...prev, mode: option.value }))}
                            aria-pressed={isActive}
                            className={`flex h-full flex-col gap-2 rounded-xl border p-4 text-left transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 ${isActive
                              ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-sm'
                              : 'border-gray-200 hover:border-purple-300 hover:shadow-sm'
                              }`}
                          >
                            <span className="text-2xl">{option.emoji}</span>
                            <div>
                              <p className="font-semibold text-gray-900">{option.label}</p>
                              <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
                    <header className="space-y-1">
                      <h4 className="text-base font-semibold text-gray-900">{select({ es: 'Color principal', en: 'Primary color' })}</h4>
                      <p className="text-sm text-gray-500">{select({ es: 'Selecciona el color que dará personalidad a la interfaz.', en: 'Pick the accent color that represents you.' })}</p>
                    </header>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                      {colorOptions.map((color) => {
                        const isActive = themeForm.primaryColor === color;
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setThemeForm((prev) => ({ ...prev, primaryColor: color }))}
                            aria-pressed={isActive}
                            className={`relative flex h-12 items-center justify-center rounded-xl border transition-transform focus:outline-none focus:ring-2 focus:ring-purple-500 ${isActive
                              ? 'border-transparent ring-2 ring-purple-500 scale-105'
                              : 'border-gray-200 hover:scale-105'
                              }`}
                            style={{ backgroundColor: color }}
                          >
                            {isActive && <Check size={16} className="text-white drop-shadow" />}
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
                    <header className="space-y-1">
                      <h4 className="text-base font-semibold text-gray-900">{select({ es: 'Tipografía', en: 'Typography' })}</h4>
                      <p className="text-sm text-gray-500">{select({ es: 'Ajusta el tamaño y la familia tipográfica para sentirte cómodo.', en: 'Adjust size and font family for comfortable reading.' })}</p>
                    </header>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>{select({ es: 'Tamaño de fuente', en: 'Font size' })}</span>
                        <span className="font-semibold text-gray-900">{themeForm.fontSize}px</span>
                      </div>
                      <input
                        type="range"
                        min="12"
                        max="18"
                        value={themeForm.fontSize}
                        onChange={(e) => setThemeForm((prev) => ({ ...prev, fontSize: parseInt(e.target.value, 10) }))}
                        aria-label={select({ es: 'Selecciona el tamaño de fuente', en: 'Select font size' })}
                        aria-valuemin={12}
                        aria-valuemax={18}
                        aria-valuenow={themeForm.fontSize}
                        className="w-full h-2 rounded-full bg-gray-200 accent-purple-500 cursor-pointer"
                      />
                      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
                        <p className="text-xs text-gray-500 mb-1">{select({ es: 'Vista previa', en: 'Preview' })}</p>
                        <p
                          style={{ fontFamily: themeForm.fontFamily, fontSize: themeForm.fontSize }}
                          className="font-medium text-gray-700"
                        >
                          Soartistic · Aa Bb Cc 123
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {fontOptions.map((font) => {
                          const isActive = themeForm.fontFamily === font;
                          return (
                            <button
                              key={font}
                              type="button"
                              onClick={() => setThemeForm((prev) => ({ ...prev, fontFamily: font }))}
                              aria-pressed={isActive}
                              className={`flex flex-col items-start gap-1 rounded-xl border p-3 transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 ${isActive
                                ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-sm'
                                : 'border-gray-200 text-gray-700 hover:border-purple-300 hover:shadow-sm'
                                }`}
                              style={{ fontFamily: font }}
                            >
                              <span className="text-lg leading-none">Aa</span>
                              <span className="text-xs uppercase tracking-widest text-gray-500">{font}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                </div>

                <button
                  onClick={handleSaveTheme}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 touch-target text-base font-medium"
                >
                  <Save size={16} />
                  {select({ es: 'Aplicar cambios', en: 'Apply changes' })}
                </button>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <SecurityTab />
            )}

            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{select({ es: 'Configuración de privacidad', en: 'Privacy settings' })}</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {select({ es: 'Visibilidad del perfil', en: 'Profile visibility' })}
                    </label>
                    <div className="space-y-2">
                      {[{ value: 'public', label: select({ es: 'Público', en: 'Public' }), desc: select({ es: 'Cualquiera puede ver tu perfil', en: 'Anyone can view your profile' }) },
                      { value: 'followers', label: select({ es: 'Solo seguidores', en: 'Followers only' }), desc: select({ es: 'Solo tus seguidores pueden ver tu perfil completo', en: 'Only followers can view your full profile' }) },
                      { value: 'private', label: select({ es: 'Privado', en: 'Private' }), desc: select({ es: 'Tu perfil no es visible para otros', en: 'Your profile is hidden from others' }) }
                      ].map((option) => (
                        <label key={option.value} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="profile_visibility"
                            value={option.value}
                            checked={privacySettings.profile_visibility === option.value}
                            onChange={(e) => setPrivacySettings(prev => ({ ...prev, profile_visibility: e.target.value as 'public' | 'followers' | 'private' }))}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900">{option.label}</p>
                            <p className="text-sm text-gray-500">{option.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {[{ key: 'show_online_status', label: select({ es: 'Mostrar estado en línea', en: 'Show online status' }), desc: select({ es: 'Otros usuarios pueden ver cuándo estás activo', en: 'Let others know when you are active' }) },
                    { key: 'show_in_search', label: select({ es: 'Aparecer en búsquedas', en: 'Appear in search results' }), desc: select({ es: 'Tu perfil aparecerá en los resultados de búsqueda', en: 'Your profile will show in search results' }) },
                    { key: 'analytics_tracking', label: select({ es: 'Permitir analíticas', en: 'Allow analytics' }), desc: select({ es: 'Ayúdanos a mejorar con datos anónimos de uso', en: 'Help us improve with anonymous usage data' }) }
                    ].map((setting) => (
                      <div key={setting.key} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{setting.label}</p>
                          <p className="text-sm text-gray-500">{setting.desc}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={privacySettings[setting.key as keyof typeof privacySettings] as boolean}
                            onChange={(e) => setPrivacySettings(prev => ({ ...prev, [setting.key]: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {select({ es: '¿Quién puede enviarte mensajes?', en: 'Who can message you?' })}
                    </label>
                    <div className="space-y-2">
                      {[{ value: 'everyone', label: select({ es: 'Cualquiera', en: 'Anyone' }) },
                      { value: 'followers', label: select({ es: 'Solo seguidores', en: 'Followers only' }) },
                      { value: 'none', label: select({ es: 'Nadie', en: 'No one' }) }
                      ].map((option) => (
                        <label key={option.value} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="allow_messages"
                            value={option.value}
                            checked={privacySettings.allow_messages === option.value}
                            onChange={(e) => setPrivacySettings(prev => ({ ...prev, allow_messages: e.target.value as 'everyone' | 'followers' | 'none' }))}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <span className="font-medium text-gray-900">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <section className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                      <h4 className="text-base font-semibold text-gray-900">
                        {t('privacy.export.title', select({ es: 'Descarga de datos', en: 'Download your data' }))}
                      </h4>
                      <p className="text-sm text-gray-600 mt-2">
                        {t('privacy.export.description', select({
                          es: 'Obtén una copia completa de tu actividad, publicaciones y compras en SoArtistic.',
                          en: 'Get a complete copy of your activity, posts, and purchases on SoArtistic.',
                        }))}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <button
                          type="button"
                          onClick={() => handleDownloadAccountData('excel')}
                          disabled={downloadingData}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-600 px-4 py-2 text-sm font-medium text-green-600 transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                        >
                          <Download size={16} />
                          {downloadingData
                            ? t('privacy.export.progress', select({ es: '...', en: '...' }))
                            : 'Excel'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadAccountData('pdf')}
                          disabled={downloadingData}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-600 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                        >
                          <Download size={16} />
                          {downloadingData
                            ? t('privacy.export.progress', select({ es: '...', en: '...' }))
                            : 'PDF'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadAccountData('json')}
                          disabled={downloadingData}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-600 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                        >
                          <Download size={16} />
                          JSON
                        </button>
                      </div>
                    </section>

                    <section className="bg-red-50 border border-red-200 rounded-xl p-4 sm:p-6 shadow-sm">
                      <h4 className="text-base font-semibold text-red-700">
                        {t('privacy.delete.title', select({ es: 'Eliminar cuenta', en: 'Delete account' }))}
                      </h4>
                      <p className="text-sm text-red-700 mt-2">
                        {t('privacy.delete.description', select({
                          es: 'Esta acción elimina de forma permanente tu cuenta y todos tus datos asociados.',
                          en: 'This action permanently removes your account and all associated data.',
                        }))}
                      </p>
                      <p className="mt-3 flex items-start gap-2 text-sm text-red-600">
                        <AlertCircle size={16} className="mt-[2px]" />
                        <span>
                          {t('privacy.delete.warning', select({
                            es: 'La eliminación es irreversible y cerrarás sesión automáticamente.',
                            en: 'Deletion is irreversible and you will be signed out automatically.',
                          }))}
                        </span>
                      </p>
                      <label className="mt-4 block text-xs font-semibold uppercase tracking-widest text-red-700">
                        {t('privacy.delete.confirmLabel', select({ es: 'Escribe tu correo para confirmar', en: 'Type your email to confirm' }))}
                      </label>
                      <input
                        type="email"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder={t('privacy.delete.confirmPlaceholder', user?.email || 'you@email.com')}
                        className="mt-2 w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200"
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        onClick={handleDeleteAccount}
                        disabled={!canDeleteAccount || deletingAccount}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                      >
                        <Trash2 size={16} />
                        {deletingAccount
                          ? t('privacy.delete.progress', select({ es: 'Eliminando cuenta...', en: 'Deleting account...' }))
                          : t('privacy.delete.button', select({ es: 'Eliminar definitivamente mi cuenta', en: 'Permanently delete my account' }))}
                      </button>
                    </section>
                  </div>
                </div>

                <button
                  onClick={handleSavePrivacy}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 touch-target text-base font-medium"
                >
                  <Save size={16} />
                  {select({ es: 'Guardar configuración', en: 'Save settings' })}
                </button>
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && <PaymentsTab mode="payments" />}
            {activeTab === 'payout_methods' && <PaymentsTab mode="payout_methods" />}
          </div>
        </div>
      </div>
    </div >
  );
};

export default SettingsModal;
